/**
 * apps/battle/client/src/admin/UsersScreen.tsx — Users tab.
 *
 *  - Lists all users (active + soft-deleted) ordered by email
 *  - Filter by role + active flag + email substring
 *  - "New user" modal → POST /api/admin/users, surfaces one-time passphrase
 *  - Inline role + active dropdowns → PATCH /api/admin/users/:id
 *  - "Reset pwd" → POST /api/admin/users/:id/reset, surfaces new passphrase
 *  - "Deactivate" → DELETE /api/admin/users/:id (soft delete)
 *
 * Self-protection on the server prevents an admin from demoting / deactivating /
 * deleting themselves. The UI hides those actions on the current admin row to
 * make that obvious instead of letting the click surface a 409 error.
 */
import { useEffect, useState } from 'react';
import type { StudentTier } from '@econquest/shared';
import { api, ApiError, type UserRole } from '../api.js';
import type { AdminUser, CreateUserResp, ListUsersResp, ResetUserResp } from './api.js';

interface Props {
  currentAdminId: string;
  showToast: (msg: string, kind?: 'success' | 'error' | 'warn') => void;
}

const ROLES: UserRole[] = ['student', 'sme', 'admin'];
const TIERS: StudentTier[] = ['FREEMIUM', 'STANDARD', 'PLUS'];

export function UsersScreen({ currentAdminId, showToast }: Props) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active');
  const [filterEmail, setFilterEmail] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [passphraseReveal, setPassphraseReveal] = useState<{ email: string; passphrase: string } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);
    api
      .get<ListUsersResp>('/api/admin/users')
      .then((r) => { if (!cancelled) setUsers(r.users); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
        setLoadErr(msg);
      });
    return () => { cancelled = true; };
  }, [reloadTick]);

  function reload() { setReloadTick((n) => n + 1); }

  async function patchUser(
    id: string,
    body: { role?: UserRole; active?: boolean; displayName?: string; subscriptionTier?: StudentTier },
  ) {
    try {
      await api.patch<{ user: AdminUser }>(`/api/admin/users/${id}`, body);
      showToast('User updated', 'success');
      reload();
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      showToast(`Update failed: ${msg}`, 'error');
    }
  }

  function changeTier(u: AdminUser, tier: StudentTier) {
    if (tier === u.subscriptionTier) return;
    // A manual override of an active Stripe subscriber does NOT cancel their Stripe
    // sub — their card keeps being charged and a renewal webhook may overwrite this.
    if (u.subscriptionSource === 'STRIPE' && u.subscriptionTier !== 'FREEMIUM') {
      if (
        !confirm(
          "This user has an active Stripe subscription. A manual change won't stop their card from being charged and may be overwritten by Stripe. To truly cancel, use the Stripe Dashboard. Continue anyway?",
        )
      ) {
        return;
      }
    }
    void patchUser(u.id, { subscriptionTier: tier });
  }

  async function resetPassword(u: AdminUser) {
    if (!confirm(`Reset password for ${u.email}? The user will be locked out until you share the new passphrase.`)) return;
    try {
      const r = await api.post<ResetUserResp>(`/api/admin/users/${u.id}/reset`);
      setPassphraseReveal({ email: u.email, passphrase: r.passphrase });
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      showToast(`Reset failed: ${msg}`, 'error');
    }
  }

  async function deactivate(u: AdminUser) {
    if (!confirm(`Deactivate ${u.email}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/api/admin/users/${u.id}`);
      showToast('User deactivated', 'success');
      reload();
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      showToast(`Deactivate failed: ${msg}`, 'error');
    }
  }

  const filtered = (users ?? []).filter((u) => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterActive === 'active' && !u.active) return false;
    if (filterActive === 'inactive' && u.active) return false;
    if (filterEmail && !u.email.toLowerCase().includes(filterEmail.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="screen-users">
      <div className="card">
        <h2>Users</h2>
        <div className="toolbar">
          <input
            placeholder="Filter by email…"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ maxWidth: 220 }}
            data-testid="input-filter-email"
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as UserRole | 'all')}
            style={{ maxWidth: 130 }}
            data-testid="select-filter-role"
          >
            <option value="all">All roles</option>
            {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as typeof filterActive)}
            style={{ maxWidth: 140 }}
            data-testid="select-filter-active"
          >
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </select>
          <div className="spacer" />
          <button className="ghost sm" onClick={reload} data-testid="button-refresh-users">Refresh</button>
          <button className="primary sm" onClick={() => setShowCreate(true)} data-testid="button-new-user">
            + New user
          </button>
        </div>
        {loadErr && <div className="err">Failed to load: {loadErr}</div>}
        {!users && !loadErr && <div className="spinner">Loading users…</div>}
        {users && (
          <table className="users-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Subscription</th>
                <th>Active</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = u.id === currentAdminId;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="email">{u.email}{isSelf ? ' (you)' : ''}</td>
                    <td>{u.displayName}</td>
                    <td>
                      <select
                        value={u.role}
                        disabled={isSelf}
                        onChange={(e) => void patchUser(u.id, { role: e.target.value as UserRole })}
                        data-testid={`select-role-${u.id}`}
                      >
                        {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
                      </select>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <select
                          value={u.subscriptionTier}
                          onChange={(e) => changeTier(u, e.target.value as StudentTier)}
                          data-testid={`select-tier-${u.id}`}
                        >
                          {TIERS.map((t) => (<option key={t} value={t}>{t}</option>))}
                        </select>
                        <span className="chip">
                          {u.subscriptionSource ?? '—'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`chip ${u.active ? 'active-yes' : 'active-no'}`}>
                        {u.active ? 'yes' : 'no'}
                      </span>
                    </td>
                    <td className="muted" style={{ fontSize: 11 }}>
                      {u.lastLogin ? new Date(u.lastLogin).toISOString().slice(0, 16).replace('T', ' ') : '—'}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="sm"
                          onClick={() => void resetPassword(u)}
                          data-testid={`button-reset-${u.id}`}
                        >
                          Reset pwd
                        </button>
                        {!isSelf && u.active && (
                          <button
                            className="danger sm"
                            onClick={() => void deactivate(u)}
                            data-testid={`button-deactivate-${u.id}`}
                          >
                            Deactivate
                          </button>
                        )}
                        {!isSelf && !u.active && (
                          <button
                            className="sm"
                            onClick={() => void patchUser(u.id, { active: true })}
                            data-testid={`button-reactivate-${u.id}`}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ padding: 12 }}>No users match the filter.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(resp) => {
            setShowCreate(false);
            setPassphraseReveal({ email: resp.user.email, passphrase: resp.passphrase });
            reload();
          }}
          onError={(msg) => showToast(`Create failed: ${msg}`, 'error')}
        />
      )}

      {passphraseReveal && (
        <PassphraseRevealModal
          email={passphraseReveal.email}
          passphrase={passphraseReveal.passphrase}
          onClose={() => setPassphraseReveal(null)}
        />
      )}
    </div>
  );
}

// ---- create-user modal -----------------------------------------------------

function CreateUserModal({
  onClose,
  onCreated,
  onError,
}: {
  onClose: () => void;
  onCreated: (resp: CreateUserResp) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('student');
  const [customPwd, setCustomPwd] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!email || !email.includes('@')) { onError('email looks invalid'); return; }
    if (!displayName.trim()) { onError('display name is required'); return; }
    setBusy(true);
    try {
      const body: Record<string, string> = {
        email: email.trim(),
        displayName: displayName.trim(),
        role,
      };
      if (customPwd && customPwd.length >= 8) body.passphrase = customPwd;
      const resp = await api.post<CreateUserResp>('/api/admin/users', body);
      onCreated(resp);
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      onError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="modal-create-user">
        <h2>Create user</h2>
        <label>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" data-testid="input-new-email" />
        <label>Display name</label>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Alice Smith" data-testid="input-new-name" />
        <label>Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} data-testid="select-new-role">
          {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
        <label>Custom passphrase (optional — leave blank to auto-generate)</label>
        <input
          value={customPwd}
          onChange={(e) => setCustomPwd(e.target.value)}
          placeholder="≥ 8 chars, or leave blank"
          data-testid="input-new-passphrase"
        />
        <div className="modal-actions">
          <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={busy} data-testid="button-submit-create">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- show-once passphrase modal -------------------------------------------

function PassphraseRevealModal({
  email,
  passphrase,
  onClose,
}: {
  email: string;
  passphrase: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(passphrase);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="modal-passphrase">
        <h2>Passphrase for {email}</h2>
        <div className="passphrase-box">
          <span className="warn">Shown once — copy now and share via a secure channel.</span>
          <span data-testid="text-passphrase">{passphrase}</span>
        </div>
        <div className="modal-actions">
          <button className="ghost" onClick={() => void copy()} data-testid="button-copy-passphrase">
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button className="primary" onClick={onClose} data-testid="button-close-passphrase">Done</button>
        </div>
      </div>
    </div>
  );
}
